import { Node, mergeAttributes } from '@tiptap/core'
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react'

// Shared ref updated by the editor when merge tags are fetched.
// This avoids the stale-options issue where TipTap extensions
// capture options at mount time and never re-read them.
let _mergeTagsRef = []
export function setMergeTagsRef(tags) { _mergeTagsRef = tags || [] }

function MergeTagPill({ node, selected }) {
    const tags = _mergeTagsRef
    const def = tags.find((t) => t.tag === `{{${node.attrs.tag}}}`)
    const label = def?.label ?? node.attrs.tag

    // Pill rendered as display:inline with inherited line-height so it does
    // NOT inflate the parent paragraph's line-box. Previously used
    // inline-flex + padding + line-height 1.6 — that made merge-tag-
    // containing lines visibly taller than plain-text lines, so pressing
    // Enter after a merge-tag line felt like a bigger paragraph gap.
    // Horizontal padding only; no vertical padding or borders that could
    // push the line-box taller than surrounding text.
    return (
        <NodeViewWrapper as="span" style={{ display: 'inline', lineHeight: 'inherit' }}>
            <span
                style={{
                    display: 'inline',
                    padding: '0 6px',
                    margin: '0',
                    backgroundColor: selected ? '#99f6e4' : '#ccfbf1',
                    color: '#115E59',
                    borderRadius: '9999px',
                    fontSize: '0.85em',
                    fontFamily: 'ui-monospace, Consolas, monospace',
                    lineHeight: 'inherit',
                    whiteSpace: 'nowrap',
                    cursor: 'default',
                    userSelect: 'text',
                    boxShadow: selected ? '0 0 0 2px #99f6e4' : 'none',
                }}
                title={`{{${node.attrs.tag}}}`}
            >
                {label}
            </span>
        </NodeViewWrapper>
    )
}

export const MergeTagNode = Node.create({
    name: 'mergeTag',
    group: 'inline',
    inline: true,
    atom: true,
    selectable: true,
    draggable: false,

    addOptions() {
        return { mergeTags: [] }
    },

    addAttributes() {
        return {
            tag: { default: '' },
        }
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-type="mergeTag"]',
                getAttrs: (el) => ({ tag: el.getAttribute('data-tag') || '' }),
            },
        ]
    },

    renderHTML({ node, HTMLAttributes }) {
        return [
            'span',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'mergeTag',
                'data-tag': node.attrs.tag,
            }),
            `{{${node.attrs.tag}}}`,
        ]
    },

    addNodeView() {
        return ReactNodeViewRenderer(MergeTagPill)
    },

    addCommands() {
        return {
            insertMergeTag:
                (attrs) =>
                ({ chain }) =>
                    chain()
                        .insertContent({ type: this.name, attrs })
                        .insertContent(' ')
                        .run(),
        }
    },

    addKeyboardShortcuts() {
        return {
            Backspace: ({ editor }) => {
                const { state } = editor
                const { $from, empty } = state.selection
                if (!empty) return false
                if ($from.parentOffset !== 0) return false
                const posBefore = $from.before($from.depth)
                if (posBefore <= 0) return false
                const resolved = state.doc.resolve(posBefore - 1)
                const nodeBefore = resolved.nodeBefore
                if (nodeBefore?.type?.name !== 'mergeTag') return false

                const currentNode = $from.parent
                if (currentNode.content.size === 0) {
                    editor.commands.deleteNode('paragraph')
                    return true
                }
                // Previous block ends with a merge tag atom and current block has
                // content. The default joinBackward can swallow the trailing atom
                // when merging; do a controlled tr.join at the block boundary so
                // all inline nodes (including the atom) survive the merge.
                try {
                    const tr = state.tr.join(posBefore)
                    editor.view.dispatch(tr.scrollIntoView())
                    return true
                } catch (_err) {
                    return false
                }
            },
        }
    },
})
